import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { VEHICLE_CLASSES } from "@/lib/vehicleClasses";

// Images come from the central vehicle catalogue (lib/vehicleClasses.js) —
// swap them there when real fleet photography is available.
const IMG_BY_ID = Object.fromEntries(VEHICLE_CLASSES.map((v) => [v.id, v.image]));

const FEATURED = [
  {
    id: "first_class_sedan",
    name: "First Class Sedan",
    tagline: "Our finest sedan.",
    seats: "Up to 3",
    luggage: "3 checked",
  },
  {
    id: "luxury_suv",
    name: "Luxury SUV",
    tagline: "Elevated space and presence.",
    seats: "Up to 4",
    luggage: "5 checked",
  },
  {
    id: "luxury_van",
    name: "Luxury Van",
    tagline: "For larger parties.",
    seats: "Up to 14",
    luggage: "18 checked",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function FleetTeaser() {
  return (
    <section
      data-testid="fleet-teaser-section"
      className="bg-near-black text-warm-white py-24 md:py-32"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
        <motion.div
          {...fadeUp}
          className="grid md:grid-cols-2 gap-10 md:gap-16 items-end mb-16 md:mb-24"
        >
          <div>
            <p className="sjc-eyebrow text-gold flex items-center gap-4">
              <span className="sjc-hairline w-8" />
              The Fleet
            </p>
            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em]">
              A considered fleet,
              <br />
              <span className="italic font-light"> immaculately kept.</span>
            </h2>
          </div>
          <p className="text-warm-white/65 text-base md:text-lg leading-[1.75] font-light max-w-lg md:justify-self-end">
            Every vehicle is detailed daily, mechanically maintained on a strict schedule
            and equipped for a composed, quiet journey. Choose the class that best suits
            your itinerary.
            <span
              data-testid="fleet-teaser-imagery-note"
              className="mt-4 block text-warm-white/40 italic font-light"
              style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "15px" }}
            >
              Imagery is representative of each vehicle class.
            </span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {FEATURED.map((v, i) => (
            <motion.article
              key={v.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`fleet-teaser-card-${v.id}`}
              className="group relative"
            >
              <Link to="/fleet" className="block">
                <div className="relative overflow-hidden bg-surface-dark">
                  <img
                    src={IMG_BY_ID[v.id]}
                    alt={v.name}
                    className="w-full h-[420px] md:h-[520px] object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.parentElement.classList.add(
                        "bg-gradient-to-br",
                        "from-surface-dark",
                        "to-near-black",
                      );
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-near-black/85 via-near-black/10 to-transparent" />

                  <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                    <p className="sjc-eyebrow text-gold/85">Class 0{i + 1}</p>
                    <h3 className="font-display mt-3 text-[28px] md:text-[32px] leading-tight text-warm-white">
                      {v.name}
                    </h3>
                    <p className="mt-1.5 text-warm-white/60 text-sm italic font-light">
                      {v.tagline}
                    </p>
                    <div className="mt-5 flex items-center gap-6 text-[11px] tracking-[0.22em] uppercase text-warm-white/60 font-medium">
                      <span>{v.seats}</span>
                      <span className="w-1 h-1 rounded-full bg-gold/70" />
                      <span>{v.luggage}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-14 md:mt-20 flex justify-center"
        >
          <Link
            to="/fleet"
            data-testid="fleet-teaser-view-all-cta"
            className="group inline-flex items-center gap-4 text-warm-white/85 hover:text-warm-white text-[11px] tracking-[0.28em] uppercase font-medium transition-colors duration-500"
          >
            <span className="w-10 h-px bg-gold transition-all duration-500 group-hover:w-16" />
            <span>Explore the full fleet</span>
            <span className="w-10 h-px bg-gold transition-all duration-500 group-hover:w-16" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
